import * as z from "zod";
import {
  regexEmail,
  regexNameWithAnd,
  regexTelephone,
  RegexMoney,
  regexSurnameExclusions,
  regexFornameExclusions,
} from "@fwa/src/utils/formUtils";

import { addressValidation } from "@fwa/src/validation/address";
import { suppresionValidation } from "@fwa/src/validation/suppressions";

export const donateValidation = z
  .object({
    title: z.enum(["", "Mr", "Mrs", "Miss", "Ms", "Mx", "Other"]).optional(),
    titleOther: z
      .string()
      .min(2, "Title should be at least 2 characters long.")
      .max(50, "Title should be at most 50 characters long.")
      .regex(regexNameWithAnd.pattern, `Title ${regexNameWithAnd.message}`)
      .optional(),
    forename: z
      .string()
      .trim()
      .min(1, "First name field is required.")
      .min(
        2,
        "First name should be at least 2 characters long. You can add a different name on your donation later.",
      )
      .max(
        50,
        "First name should be at most 50 characters long. You can add a different name on your donation later.",
      )
      .regex(
        regexFornameExclusions.pattern,
        "Please only enter your formal name here. You can add a different name on your donation later.",
      )
      .regex(
        /^(?!.*(^and | and | and$|&|\+))/i,
        "Please enter only one person's name.",
      )
      .regex(
        regexNameWithAnd.pattern,
        `First name ${regexNameWithAnd.message} You can add a different name on your donation later.`,
      ),
    surname: z
      .string()
      .trim()
      .min(1, "Last name field is required.")
      .min(
        2,
        "Last name should be at least 2 characters long. You can add a different name on your donation later.",
      )
      .max(
        50,
        "Last name should be at most 50 characters long. You can add a different name on your donation later.",
      )
      .regex(
        regexSurnameExclusions.pattern,
        "Please only enter your formal name here. You can add a different name on your donation later.",
      )
      .regex(
        /^(?!.*(^and | and | and$|&|\+))/i,
        "Please enter only one person's name.",
      )
      .regex(
        regexNameWithAnd.pattern,
        `Last name ${regexNameWithAnd.message} You can add a different name on your donation later.`,
      ),
    email: z
      .email()
      .trim()
      .min(1, "Email address field is required.")
      .min(7, "Email address should be at least 7 characters long.")
      .max(50, "Email address should be at most 50 characters long.")
      .regex(regexEmail.pattern, regexEmail.message),
    phoneNumber: z
      .string()
      .transform((value) => value.replace(/\s+/g, ""))
      .pipe(
        z
          .string()
          .trim()
          .min(10, "Phone number should be at least 10 characters long.")
          .max(16, "Phone number should be at most 16 characters long.")
          .regex(regexTelephone.pattern, regexTelephone.message)
          .or(z.literal("")), // allow empty
      ),
    amount: z
      .string()
      .min(1, "Amount field is required.")
      .regex(RegexMoney.pattern, RegexMoney.message)
      .refine(
        (value: string | undefined) =>
          value?.length ? parseFloat(value) > 1.99 : true,
        "Please enter an amount between 2 and 9999999 inclusive.",
      )
      .refine(
        (value: string | undefined) =>
          value?.length ? parseFloat(value) < 9999999.01 : true,
        "Please enter an amount between 2 and 9999999 inclusive.",
      ),
    donationName: z
      .string()
      .max(101, "Display name should be at most 101 characters long.")
      .optional(),
    donationMessage: z
      .string()
      .max(150, "Message should be at most 150 characters long.")
      .optional(),
    toBeGiftAided: z.boolean(),
    personalGiftAid: z.enum(["true", "false"]).nullable(),
    noBenefitReceived: z.enum(["true", "false"]).nullable(),
    // Address
    address: addressValidation,
    suppressions: suppresionValidation,
  })
  .refine(
    // custom title
    (data) =>
      data.title === "Other" ? (data.titleOther?.length || 0) >= 2 : true,
    {
      error: "Title should be at least 2 characters long.",
      path: ["titleOther"],
    },
  )
  .refine(
    // phone number only required if selected notifications require it
    (data) =>
      ["LIPH"].includes(data.suppressions.phone) ||
      ["PYSM"].includes(data.suppressions.text)
        ? data.phoneNumber?.length
        : true,
    {
      error:
        'Phone number is required because staying in touch by phone or text message is set to "Yes".',
      path: ["phoneNumber"],
    },
  );

export default donateValidation;
